import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import { composePath } from '@culture/common-helpers/params';
import PropTypes from 'prop-types';
import _ from 'underscore';
import dynamic from 'next/dynamic';
import { publicationTypesHash } from '@culture/utils/helpers/publications';
import { statusesHash } from '@culture/utils/helpers/statuses';
import { useIsMobile } from 'hooks/useIsMobile';

import { TagPropType } from 'propTypes/common';
import { sortOptions } from 'components/filters/SortFilters';
import { FiltersValuesType } from '../propTypes';
import TagList from './TagList';

const MobileControls = dynamic(() => import('./MobileControls'), {
	ssr: false
});

const DesktopControls = dynamic(() => import('./DesktopControls'), {
	ssr: false
});

const filtersOptions = {
	rubrics: [
		{
			title: 'Театры',
			name: 'theaters'
		},
		{
			title: 'Музеи',
			name: 'museums'
		},
		{
			title: 'Кино',
			name: 'cinema'
		},
		{
			title: 'Музыка',
			name: 'music'
		},
		{
			title: 'Архитектура',
			name: 'architecture'
		},
		{
			title: 'Литература',
			name: 'literature'
		},
		{
			title: 'Традиции',
			name: 'traditions'
		},
		{
			title: 'Лекции',
			name: 'lectures'
		}
	],
	sort: sortOptions
};

const Controls = ({ urlParams, tags, filtersValues, defaultFiltersValues }) => {
	const isMobile = useIsMobile();
	const { rubricId, tagId } = filtersValues;
	const { push, pathname } = useRouter();

	const filters = {
		..._(urlParams).omit('page')
	};

	const changeUrl = (params) => {
		const { rubric, withPodcasts, tag, ...restParams } = params;

		const basePath = '/publications';

		const isRubricReset = rubric === '';
		if (isRubricReset) {
			return push(basePath);
		}
		const isNewRubric = rubric && rubric !== filters?.rubric;

		if (isNewRubric) {
			return push(`${basePath}/${rubric}`);
		}
		let prefix = rubric ? `${basePath}/${rubric}` : basePath;

		if (tag) {
			prefix += `/tag-${tag}`;
		}

		if (withPodcasts) {
			prefix += '/withPodcast-true';
		}

		const filterParams = {
			...restParams,
			page: null
		};

		const path = composePath(prefix, filterParams, {});
		return push(path);
	};

	const handleFilterChange = useCallback(
		(name, value) => {
			const params = {
				...urlParams,
				[name]: value,
				page: null
			};

			changeUrl(params);
		},
		[urlParams]
	);

	const handleFilterReset = useCallback(() => {
		push({ pathname });
	}, [pathname, push]);

	const tagList = (
		<TagList
			filters={filters}
			onFilterChange={handleFilterChange}
			tags={tags || []}
			tagCountOnMobile={6}
			apiParams={{
				url: 'tags/collection',
				affectQueryParams: {
					collectionName: 'publications',
					sort: '-count',
					types: [publicationTypesHash.articles, publicationTypesHash.themes],
					statuses: [statusesHash.published],
					withPodcast: filters?.withPodcasts,
					...(rubricId && { rubricPaths: [rubricId] }),
					...(tagId && { tags: [tagId] })
				}
			}}
			featuredTags={tags}
		/>
	);

	return (
		<>
			{isMobile ? (
				<MobileControls
					onFilterChange={handleFilterChange}
					onFilterReset={handleFilterReset}
					{...{
						filters,
						tagList,
						isMobile,
						filtersOptions,
						defaultFiltersValues
					}}
				/>
			) : (
				<DesktopControls
					onFilterChange={handleFilterChange}
					onFilterReset={handleFilterReset}
					{...{ filters, tagList, isMobile, filtersOptions }}
				/>
			)}
		</>
	);
};

Controls.propTypes = {
	urlParams: PropTypes.object.isRequired,
	tags: PropTypes.arrayOf(TagPropType),
	filtersValues: FiltersValuesType,
	defaultFiltersValues: PropTypes.object
};

export default Controls;
